import React from 'react'
import { PropTypes } from 'prop-types'
import { SortableContainer, SortableElement } from 'react-sortable-hoc'
import classNames from 'classnames'
import FilterBar from './FilterBar'
import displayModes, { allDisplayModes } from '../enums/displayModes'
import styles from './Collection.icss'
import ContextMenu from './ContextMenu'

const propForItem = (prop, item, key) => (typeof prop === 'function' ? prop(item, key) : prop)

const bindClickHandlerToItem = (handler, item, index) => (e) => {
	e.preventDefault()
	handler(item, index)
}

// TODO: items across
// TODO: make display mode controlled, via control prop, with change event
// TODO: make display mode persistent, stored in redux state by caller
// TODO: better loading states from the item components

class Collection extends React.Component {
	constructor(props) {
		super(props)

		this.state = {
			anchorEl: null,
			item: null,
			key: null,
			displayMode:
				props.defaultDisplayMode ||
				(props.allowedDisplayModes && props.allowedDisplayModes[0]) ||
				displayModes.LIST.type
		}

		this.bindActionAndClose = this.bindActionAndClose.bind(this)
		this.contextClickHandlerForItem = this.contextClickHandlerForItem.bind(this)
		this.collectionContainer = this.collectionContainer.bind(this)
		this.getAllowedDisplayModes = this.getAllowedDisplayModes.bind(this)
		this.handleContextClose = this.handleContextClose.bind(this)
		this.renderContextMenu = this.renderContextMenu.bind(this)
		this.renderItems = this.renderItems.bind(this)
		this.setDisplayMode = this.setDisplayMode.bind(this)
	}

	getAllowedDisplayModes() {
		return this.props.allowAllDisplayModes ? allDisplayModes : this.props.allowedDisplayModes
	}

	setDisplayMode(displayMode) {
		this.setState({ displayMode })
		if (this.props.onDisplayModeChange) {
			this.props.onDisplayModeChange(displayMode)
		}
	}

	handleContextClose() {
		this.setState({ anchorEl: null, item: null, key: null })
	}


	bindActionAndClose(action) {
		const { item, key } = this.state

		return () => {
			action(item, key)
			this.handleContextClose()
		}
	}

	contextClickHandlerForItem(item, key) {
		return (e) => {
			this.setState({ anchorEl: e.currentTarget, item, key })
		}
	}

	collectionContainer(props) {
		const { displayMode } = this.state
		const { isLoading, filterOptions, onFilterChange, filterDefault, renderWhenNoItems, items, classes } = this.props
		const ItemComponent = displayModes[displayMode].component
		return (renderWhenNoItems && (!items || items.length === 0)) ? renderWhenNoItems(props) : (
			<div className={classNames(styles.container, { [classes.container]: classes && classes.container })}>
				<FilterBar
					displayMode={this.state.displayMode}
					setDisplayMode={this.setDisplayMode}
					allowedDisplayModes={this.getAllowedDisplayModes()}
					filterOptions={filterOptions}
					onFilterChange={onFilterChange}
					filterDefault={filterDefault}
				/>
				<div className={classNames(styles.collection, styles[`collection${displayMode}`], { [classes.itemsContainer]: classes && classes.itemsContainer })}>
					{isLoading && [...Array(10)].map((x, i) => (
						<ItemComponent key={`loading_${i}`} /> // eslint-disable-line react/no-array-index-key
					))}
					{!isLoading && props.renderItems()}
					{props.renderContextMenu()}
				</div>
			</div>
		)
	}

	renderItems() {
		const { children, classes, contextMenuItems, items, itemOnSelect, itemHref, itemImageContent, itemImageCover, itemCounts, itemImageClassName, itemKey, itemIconName, itemIconNameFunc, itemTitle, itemFitScoreTesting, itemSubtitle, itemDescription, itemDetail, renderItemFragment, renderAfterItem, renderAfterLastItem, showResultsSeparator, itemOwn, itemWant, itemOwnSet, itemWantSet, itemShowOwnWant, itemShowFollow, itemShowCountComments, itemCountComments, itemCountCommentsClick, itemShareClick, itemKudos, itemShowKudos, itemKudosSet, itemKudosCount, itemFollow, itemFollowSet } = this.props
		const { displayMode } = this.state
		const ItemComponent = this.props.sortable ? SortableElement(({ itemProps }) => {
			const MyComponent = displayModes[displayMode].component
			return <MyComponent {...itemProps} />
		}) : displayModes[displayMode].component

		if (typeof items === 'object') {
			return (
				<React.Fragment>
					{showResultsSeparator && (
						<div className={styles.borderBottom} />
					)}
					{Object.keys(items).map((key, index) => {
						const item = items[key]
						const renderProps = { item, key, index, displayMode, onContextClick: this.contextClickHandlerForItem(item, key) }
						if (typeof children === 'function') {
							return children(renderProps)
						}
						// TODO: find a better way to pass props to some component only - e.g. own/want to FULL, itemsAcross to GRID... maybe could configure them in the displayModes?
						const itemProps = {
							classes: { root: classes.itemRoot },
							description: propForItem(itemDescription, item),
							detail: propForItem(itemDetail, item), // TODO: handle when detail defined
							href: propForItem(itemHref, item),
							imageContent: propForItem(itemImageContent, item),
							counts: propForItem(itemCounts, item),
							imageClassName: propForItem(itemImageClassName, item),
							itemFragment: propForItem(renderItemFragment, renderProps),
							renderAfterItem: propForItem(renderAfterItem, renderProps),
							iconName: itemIconNameFunc ? itemIconNameFunc(propForItem(itemIconName, item), item, displayMode) : propForItem(itemIconName, item),
							key: itemKey ? itemKey(item) : key,
							onClick: itemOnSelect ? bindClickHandlerToItem(itemOnSelect, item, index) : undefined,
							onContextClick: propForItem(contextMenuItems, item, key).length > 0 ? this.contextClickHandlerForItem(item, key) : undefined,
							subtitle: propForItem(itemSubtitle, item),
							title: propForItem(itemTitle, item),
							fitScoreTesting: propForItem(itemFitScoreTesting, item),
							showOwnWant: propForItem(itemShowOwnWant, item),
							showCountComments: propForItem(itemShowCountComments, item),
							countComments: propForItem(itemCountComments, item),
							countCommentsClick: propForItem(itemCountCommentsClick, item),
							shareClick: propForItem(itemShareClick, item),
							kudos: propForItem(itemKudos, item),
							showKudos: propForItem(itemShowKudos, item),
							kudosCount: propForItem(itemKudosCount, item),
							showFollow: propForItem(itemShowFollow, item),
							follow: propForItem(itemFollow, item),
							followSet: propForItem(itemFollowSet, item),
							own: propForItem(itemOwn, item),
							want: propForItem(itemWant, item),
							ownSet: propForItem(itemOwnSet, item),
							wantSet: propForItem(itemWantSet, item),
							kudosSet: propForItem(itemKudosSet, item)
						}

						if (displayMode === displayModes.LIST.type || displayMode === displayModes.GRID.type) {
							itemProps.imageCover = propForItem(itemImageCover, item)
						}

						return this.props.sortable ? <ItemComponent index={index} itemProps={itemProps} key={itemProps.key} /> : <ItemComponent {...itemProps} />
					})}
					{renderAfterLastItem && renderAfterLastItem}
				</React.Fragment>
			)
		}
		return null
	}

	renderContextMenu() {
		const { contextMenuItems } = this.props
		const { anchorEl, item } = this.state

		if (item && anchorEl && contextMenuItems) {
			return (
				<ContextMenu
					contextMenuItems={contextMenuItems}
					handleContextClose={this.handleContextClose}
					anchorEl={anchorEl}
					menuItemClickBinder={this.bindActionAndClose}
					item={item}
				/>
			)
		}
		return null
	}

	render() {
		const collectionContainerProps = {
			defaultDisplayMode: this.props.defaultDisplayMode,
			allowAllDisplayModes: this.props.allowAllDisplayModes,
			isLoading: this.props.isLoading,
			filterOptions: this.props.filterOptions,
			renderContextMenu: this.renderContextMenu,
			renderItems: this.renderItems
		}

		const CollectionContainer = this.collectionContainer

		if (this.props.sortable) {
			const WrappedComponent = SortableContainer(CollectionContainer)
			return (
				<WrappedComponent
					{...collectionContainerProps}
					{...this.props.sortableProps}
					axis="y"
					useDragHandle
					useWindowAsScrollContainer
				/>
			)
		}
		return <CollectionContainer {...collectionContainerProps} />
	}
}

Collection.propTypes = {
	allowAllDisplayModes: PropTypes.bool,
	allowedDisplayModes: PropTypes.arrayOf(PropTypes.oneOf(Object.keys(displayModes).map(mode => displayModes[mode].type))),
	children: PropTypes.func,
	classes: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	contextMenuItems: PropTypes.oneOfType([PropTypes.func, PropTypes.array]),
	defaultDisplayMode: PropTypes.oneOf(Object.keys(displayModes).map(mode => displayModes[mode].type)),
	filterOptions: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	onFilterChange: PropTypes.func,
	onDisplayModeChange: PropTypes.func,
	filterDefault: PropTypes.any, // eslint-disable-line react/forbid-prop-types
	isLoading: PropTypes.bool,
	itemDescription: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
	itemDetail: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
	itemHref: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
	itemImageContent: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.string,
		PropTypes.arrayOf(PropTypes.string),
		PropTypes.shape({ type: PropTypes.string, webpurl: PropTypes.string, url: PropTypes.string }),
		PropTypes.arrayOf(PropTypes.shape({ type: PropTypes.string, webpurl: PropTypes.string, url: PropTypes.string }))
	]),
	itemImageCover: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
	itemImageClassName: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
	itemKey: PropTypes.func,
	itemOnSelect: PropTypes.func,
	items: PropTypes.oneOfType([PropTypes.array, PropTypes.object]), // eslint-disable-line react/forbid-prop-types
	itemCounts: PropTypes.oneOfType([PropTypes.func, PropTypes.array]), // eslint-disable-line react/forbid-prop-types
	itemSubtitle: PropTypes.oneOfType([PropTypes.func, PropTypes.node, PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
	itemTitle: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
	itemFitScoreTesting: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
	itemIconName: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
	itemIconNameFunc: PropTypes.func,
	renderItemFragment: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
	renderWhenNoItems: PropTypes.func,
	renderAfterItem: PropTypes.func,
	renderAfterLastItem: PropTypes.node,
	showResultsSeparator: PropTypes.bool,
	sortable: PropTypes.bool,
	sortableProps: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	itemShowOwnWant: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
	itemShowCountComments: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
	itemCountComments: PropTypes.oneOfType([PropTypes.func, PropTypes.number]),
	itemCountCommentsClick: PropTypes.func,
	itemShareClick: PropTypes.func,
	itemKudos: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
	itemShowKudos: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
	itemKudosCount: PropTypes.oneOfType([PropTypes.func, PropTypes.number]),
	itemKudosSet: PropTypes.func,
	itemShowFollow: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
	itemFollow: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
	itemFollowSet: PropTypes.func,
	itemOwn: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
	itemWant: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
	itemOwnSet: PropTypes.func,
	itemWantSet: PropTypes.func
}

Collection.defaultProps = {
	allowAllDisplayModes: false,
	allowedDisplayModes: [],
	children: undefined,
	classes: {},
	contextMenuItems: [],
	defaultDisplayMode: undefined,
	filterOptions: undefined,
	onFilterChange: undefined,
	onDisplayModeChange: undefined,
	filterDefault: undefined,
	isLoading: false,
	itemDescription: undefined,
	itemDetail: undefined,
	itemHref: undefined,
	itemImageContent: undefined,
	itemImageCover: false,
	itemImageClassName: undefined,
	itemKey: undefined,
	itemOnSelect: undefined,
	items: undefined,
	itemCounts: undefined,
	itemSubtitle: undefined,
	itemTitle: undefined,
	itemFitScoreTesting: undefined,
	itemIconName: undefined,
	itemIconNameFunc: undefined,
	renderItemFragment: undefined,
	renderWhenNoItems: undefined,
	renderAfterItem: undefined,
	renderAfterLastItem: undefined,
	showResultsSeparator: false,
	sortable: false,
	sortableProps: {},
	itemShowOwnWant: false,
	itemShowCountComments: false,
	itemCountComments: undefined,
	itemCountCommentsClick: () => {},
	itemShareClick: () => {},
	itemKudos: undefined,
	itemShowKudos: false,
	itemKudosCount: undefined,
	itemKudosSet: () => {},
	itemShowFollow: false,
	itemFollow: false,
	itemFollowSet: () => {},
	itemOwn: false,
	itemWant: false,
	itemOwnSet: () => {},
	itemWantSet: () => {}
}

export default Collection



// WEBPACK FOOTER //
// ./src/components/Collection.jsx